/**
 * Pure, time-injected flap-debounce for the realtime connection surfaces
 * (RealtimeConnectionToast, OfflineIndicator) — extracted so the "when do
 * we surface a connection loss?" logic is testable without a React renderer,
 * a toast context, or real timers.
 *
 * WHY this is debounced (BUG-FE-WRITE-FALSE-NEGATIVE-UI-DESYNC-2026-06-09, P1):
 * Supabase Realtime flickers between 'reconnecting' and 'connected' during
 * brief sub-second network hiccups. A visit-report write that landed
 * server-side (201, both rows, no dup) still rendered "Verbindung verloren"
 * because the connection-status surfaces reacted to a transient flap, not the
 * sustained state.
 *
 * Contract (supersedes the un-debounced connectionToastLogic):
 *   - A LOST signal (connected → reconnecting/disconnected) is held *pending*
 *     for a {@link FLAP_GRACE_MS} grace window. It does NOT surface immediately.
 *   - If the connection recovers inside that window the pending loss is
 *     swallowed — no false failure is ever rendered.
 *   - A loss that persists past the window surfaces loud (D-33), once.
 *   - RESTORED surfaces immediately, but only after a LOST was actually shown.
 *
 * The window is driven by an injected `now` (ms epoch) so tests can advance
 * time deterministically; the React layer wires a timer to call {@link tickFlap}.
 *
 * 2026-10-27 (operator, Mission Control): the 1200 ms window still toasted
 * "Verbindung verloren / offline" several times a session on a WORKING network.
 * That toast was wrong twice: the realtime socket only drives cache REFRESH —
 * writes go through the Service Layer and offline is decided by the
 * reachability probe (`@appic/lib` offline/reachability.ts), never by this
 * socket. So a socket drop is "live updates paused", it is not offline, and it
 * only deserves a toast when it is sustained (≥ 30 s) AND the user can see the
 * tab: a loss that elapses while the tab is hidden stays pending and surfaces
 * only if it is still lost once the tab is visible again.
 */

import type { RealtimeConnectionStatus } from '@appic/hooks'

/**
 * Grace window a realtime-socket loss must persist before it surfaces. 30 s:
 * Supabase Realtime reconnects on its own within a few seconds on a healthy
 * network (heartbeat + backoff), so anything shorter toasts routine reconnects.
 */
export const FLAP_GRACE_MS = 30_000

export type FlapAction = { kind: 'lost' } | { kind: 'restored' } | { kind: 'none' }

export interface FlapState {
  /** true iff a "connection lost" surface has fired and not yet been paired with a restore */
  lostShown: boolean
  /**
   * Epoch ms (from the injected clock) at which the current loss started
   * pending, or null when there is no unsurfaced pending loss. While set and
   * not yet elapsed, the loss is held back; recovery inside the window clears
   * it silently.
   */
  pendingLostSince: number | null
}

export const INITIAL_FLAP_STATE: FlapState = { lostShown: false, pendingLostSince: null }

const isLostStatus = (s: RealtimeConnectionStatus): boolean =>
  s === 'disconnected' || s === 'reconnecting'

/**
 * Fold an observed status transition into the flap state.
 *
 * Does NOT itself surface a LOST toast — it only opens (or closes) the pending
 * grace window. {@link tickFlap} surfaces the loss once the window elapses.
 * This keeps a transition that recovers within the window from ever firing.
 */
export function reduceFlap(
  prevStatus: RealtimeConnectionStatus | null,
  nextStatus: RealtimeConnectionStatus,
  state: FlapState,
  now: number,
): { action: FlapAction; state: FlapState } {
  // Recovery: connection is healthy again.
  if (nextStatus === 'connected') {
    // A loss that was still inside its grace window never surfaced — swallow it.
    if (state.lostShown) {
      return { action: { kind: 'restored' }, state: { lostShown: false, pendingLostSince: null } }
    }
    return { action: { kind: 'none' }, state: { ...state, pendingLostSince: null } }
  }

  // Loss/flicker: only meaningful if we had a prior healthy observation
  // (avoids arming on the initial disconnected boot state before the channel
  // has ever subscribed) and aren't already lost/pending.
  if (isLostStatus(nextStatus)) {
    if (state.lostShown || state.pendingLostSince !== null) {
      // Already lost, or already counting down — don't re-arm (flicker suppression).
      return { action: { kind: 'none' }, state }
    }
    if (prevStatus === 'connected') {
      return { action: { kind: 'none' }, state: { ...state, pendingLostSince: now } }
    }
  }

  return { action: { kind: 'none' }, state }
}

/**
 * Advance the grace window. If a pending loss has persisted past
 * {@link FLAP_GRACE_MS} AND the tab is visible, surface the LOST action exactly
 * once and mark it shown. While hidden the loss stays pending (no toast nobody
 * sees, and no orphan "restored" toast later); the React layer ticks again on
 * `visibilitychange`. Safe to call repeatedly / idempotently.
 */
export function tickFlap(
  state: FlapState,
  now: number,
  visible: boolean,
): { action: FlapAction; state: FlapState } {
  if (state.pendingLostSince === null || state.lostShown || !visible) {
    return { action: { kind: 'none' }, state }
  }
  if (now - state.pendingLostSince >= FLAP_GRACE_MS) {
    return { action: { kind: 'lost' }, state: { lostShown: true, pendingLostSince: null } }
  }
  return { action: { kind: 'none' }, state }
}
